/*
 * <search-recent>
 *
 * Recent search terms (chips) and recently viewed products (tiles) inside a
 * search panel, visible only while the panel's search input is empty.
 *
 * Both lists live in localStorage (per browser). Terms are recorded on search
 * form submit, on picking a predictive result and on landing on the search page;
 * products are recorded from the product page via window.recentlyViewedSeed
 * (set in layout/theme.liquid).
 */
const RecentStore = {
  keys: { terms: 'ht:recent-searches', products: 'ht:recently-viewed' },
  limits: { terms: 6, products: 8 },

  read(type) {
    try {
      const list = JSON.parse(localStorage.getItem(this.keys[type]) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  },

  write(type, list) {
    try {
      localStorage.setItem(this.keys[type], JSON.stringify(list.slice(0, this.limits[type])));
    } catch (e) {
      /* storage unavailable (private mode) — history just isn't kept */
    }
  },

  addTerm(term) {
    term = (term || '').trim().replace(/\s+/g, ' ');
    if (term.length < 2) return;
    const lower = term.toLowerCase();
    this.write('terms', [term, ...this.read('terms').filter((t) => t.toLowerCase() !== lower)]);
  },

  removeTerm(term) {
    this.write('terms', this.read('terms').filter((t) => t !== term));
  },

  addProduct(product) {
    if (!product || !product.url) return;
    this.write('products', [product, ...this.read('products').filter((p) => p.url !== product.url)]);
  },

  clear(type) {
    this.write(type, []);
  },
};

class SearchRecent extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.closest('form');
    this.input = this.form && this.form.querySelector('input[type="search"]');
    const removeLabel = this.querySelector('[data-recent-remove-label]');
    this.removeLabel = removeLabel ? removeLabel.textContent : '[term]';

    if (this.input) {
      this.input.addEventListener('input', () => this.updateVisibility());
      this.input.addEventListener('focus', () => this.render());
      this.form.addEventListener('reset', () => setTimeout(() => this.updateVisibility()));
    }

    this.querySelectorAll('[data-recent-clear]').forEach((button) => {
      button.addEventListener('click', () => {
        RecentStore.clear(button.dataset.recentClear);
        this.render();
        this.input && this.input.focus();
      });
    });

    this.render();
  }

  render() {
    const terms = RecentStore.read('terms');
    const products = RecentStore.read('products').filter((p) => p.url !== window.location.pathname);

    this.renderTerms(terms);
    this.renderProducts(products);

    this.querySelector('[data-recent-group="terms"]').hidden = !terms.length;
    this.querySelector('[data-recent-group="products"]').hidden = !products.length;
    this.hasItems = terms.length > 0 || products.length > 0;
    this.updateVisibility();
  }

  updateVisibility() {
    const empty = !this.input || !this.input.value.trim().length;
    this.hidden = !(this.hasItems && empty);
  }

  renderTerms(terms) {
    const list = this.querySelector('[data-recent-list="terms"]');
    const searchUrl = this.dataset.searchUrl || '/search';
    list.replaceChildren(
      ...terms.map((term) => {
        const li = document.createElement('li');
        li.className = 'search-recent__chip';

        const link = document.createElement('a');
        link.className = 'search-recent__chip-link';
        link.href = `${searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        link.innerHTML = SearchRecent.clockIcon;
        const label = document.createElement('span');
        label.textContent = term;
        link.appendChild(label);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'search-recent__chip-remove';
        remove.setAttribute('aria-label', this.removeLabel.replace('[term]', term));
        remove.innerHTML = SearchRecent.closeIcon;
        remove.addEventListener('click', () => {
          RecentStore.removeTerm(term);
          this.render();
          this.input && this.input.focus();
        });

        li.append(link, remove);
        return li;
      })
    );
  }

  renderProducts(products) {
    const list = this.querySelector('[data-recent-list="products"]');
    list.replaceChildren(
      ...products.map((product) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'search-recent__product';
        link.href = product.url;

        const media = document.createElement('span');
        media.className = 'search-recent__product-media';
        if (product.image) {
          const img = document.createElement('img');
          img.src = product.image.startsWith('//') ? `https:${product.image}` : product.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 300;
          img.height = 400;
          media.appendChild(img);
        }

        const title = document.createElement('span');
        title.className = 'search-recent__product-title';
        title.textContent = product.title || '';

        link.append(media, title);
        if (product.price) {
          const price = document.createElement('span');
          price.className = 'search-recent__product-price';
          price.textContent = product.price;
          link.appendChild(price);
        }

        li.appendChild(link);
        return li;
      })
    );
  }
}

SearchRecent.clockIcon =
  '<svg width="24" height="24" class="search-recent__chip-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M3.5 10a6.5 6.5 0 1 0 1.9-4.6M3.5 3.5v2.9h2.9M10 6.5V10l2.4 1.6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
SearchRecent.closeIcon =
  '<svg width="24" height="24" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M6 6l8 8M14 6l-8 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

customElements.define('search-recent', SearchRecent);

// --- Recording ------------------------------------------------------------

// Product page seed (layout/theme.liquid).
if (window.recentlyViewedSeed) RecentStore.addProduct(window.recentlyViewedSeed);

// Landing on the search results page with a query.
if (window.recentSearchSeed) RecentStore.addTerm(window.recentSearchSeed);

// Submitting any search form (covers Enter in the header/overlay inputs).
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;
    const input = form.querySelector('input[name="q"]');
    if (input) RecentStore.addTerm(input.value);
  },
  true
);

// Picking a predictive search result — remember what was typed to find it.
document.addEventListener(
  'click',
  (event) => {
    const link = event.target.closest && event.target.closest('[data-predictive-search] a');
    if (!link) return;
    const form = link.closest('form');
    const input = form && form.querySelector('input[name="q"]');
    if (input) RecentStore.addTerm(input.value);
  },
  true
);
